import Alert from '@/components/Alert/Alert'
import TableView, { type TableSection } from '@/components/TableView'
import useCopyText from '@/components/useCopyText'
import { openShare } from '@/lib/share'
import { FormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import {
  CooperatorLink,
  createFormInviteLinkThunk,
  destroyAllFormInviteLinksThunk,
} from '@/redux/slices/formInviteLinkSlice'
import { ShallowFormRole } from '@/redux/slices/formRoleSlice'
import { ShallowForm } from '@/redux/slices/formSlice'
import { useAppDispatch } from '@/redux/store'
import { CooperatorRole, CooperatorUserRole } from '@/typings/domain'
import { keyBy, noop } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'

type Props = {
  form: ShallowForm
  formRoles: ShallowFormRole[]
  inviteLinks: CooperatorLink[]
  currentBillingAccountId: string
  formBillingAccount: FormBillingAccount
  currentUserIsOwnerOrAdmin: boolean
  currentBillingAccountName: string
}

export default function FormInviteCooperators({
  form,
  formRoles,
  inviteLinks,
  currentBillingAccountId,
  formBillingAccount,
  currentUserIsOwnerOrAdmin,
  currentBillingAccountName,
}: Props) {
  const canUseOrgInviteLink =
    formBillingAccount.abilities?.hasAddSubAccountAbility &&
    formBillingAccount.id === currentBillingAccountId &&
    currentUserIsOwnerOrAdmin

  return (
    <InviteLinkConfigurator
      formToken={form.token}
      formRoles={formRoles}
      currentBillingAccountName={currentBillingAccountName}
      inviteLinks={inviteLinks}
      canUseOrgInviteLink={!!canUseOrgInviteLink}
    />
  )
}

type InviteLinkConfiguratorProps = {
  formToken: string
  formRoles: ShallowFormRole[]
  inviteLinks: CooperatorLink[]
  currentBillingAccountName: string
  canUseOrgInviteLink: boolean
}

function InviteLinkConfigurator({
  formToken,
  formRoles,
  inviteLinks,
  currentBillingAccountName,
  canUseOrgInviteLink,
}: InviteLinkConfiguratorProps) {
  const copyText = useCopyText()
  const dispatch = useAppDispatch()

  const defaultUserRole = CooperatorUserRole.External
  const isEnabled = inviteLinks.length > 0

  const [selectedFormRole, setSelectedFormRole] = useState(
    inviteLinks[0]?.formRole?.id ||
      formRoles.find(role => role.predefined && role.originalName === CooperatorRole.DataMaintainer)?.id
  )
  const [selectedUserRole, setSelectedUserRole] = useState(
    (inviteLinks[0]?.userRole as CooperatorUserRole) ?? defaultUserRole
  )

  const userRoles = useMemo(
    () => [
      { label: '企业成员-管理员', value: CooperatorUserRole.Admin },
      { label: '企业成员-协作者', value: CooperatorUserRole.Worker },
      { label: '企业成员-查看员', value: CooperatorUserRole.Outworker },
      { label: '外部联系人', value: CooperatorUserRole.External },
    ],
    []
  )
  const formRolesMap = useMemo(() => keyBy(formRoles, 'id'), [formRoles])
  const userRolesMap = useMemo(() => keyBy(userRoles, 'value'), [userRoles])

  const currentInviteLink = useMemo(
    () => inviteLinks.find(link => link.formRole?.id === selectedFormRole && link.userRole === selectedUserRole),
    [inviteLinks, selectedFormRole, selectedUserRole]
  )

  const currentInviteLinkUrl = currentInviteLink
    ? `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/form_invites/${currentInviteLink.token}`
    : null

  useEffect(() => {
    if (!isEnabled) return
    if (currentInviteLink) return
    if (!selectedFormRole || !selectedUserRole) return

    dispatch(createFormInviteLinkThunk({ formToken, formRoleId: selectedFormRole, userRole: selectedUserRole }))
  }, [currentInviteLink, dispatch, formToken, selectedFormRole, selectedUserRole, isEnabled])

  const handleToggleSwitch = useCallback(() => {
    if (isEnabled) {
      Alert.alert('关闭邀请后所有邀请链接将失效，确认关闭？', undefined, [
        { text: '取消', style: 'cancel' },
        {
          text: '关闭',
          style: 'destructive',
          onPress: () => dispatch(destroyAllFormInviteLinksThunk({ formToken })),
        },
      ])
    } else {
      const defaultRole = formRoles.find(role => role.predefined && role.originalName === CooperatorRole.DataMaintainer)
      if (defaultRole) {
        dispatch(
          createFormInviteLinkThunk({
            formToken,
            formRoleId: defaultRole.id,
            userRole: defaultUserRole,
          })
        )
      }
    }
  }, [isEnabled, dispatch, formToken, formRoles, defaultUserRole])

  const formRoleMenuItems = useMemo(
    () =>
      formRoles.map(role => ({
        id: role.id,
        title: role.name,
        selected: selectedFormRole === role.id,
      })),
    [formRoles, selectedFormRole]
  )

  const userRoleMenuItems = useMemo(
    () =>
      userRoles.map(role => ({
        id: role.value,
        title: role.label,
        selected: selectedUserRole === role.value,
      })),
    [userRoles, selectedUserRole]
  )

  const handleFormRoleMenuItemSelect = useCallback(
    (roleId: string) => {
      setSelectedFormRole(roleId)
    },
    [setSelectedFormRole]
  )

  const handleUserRoleMenuItemSelect = useCallback(
    (roleId: string) => {
      setSelectedUserRole(roleId as CooperatorUserRole)
    },
    [setSelectedUserRole]
  )

  const handleCopyInviteLinkPress = useCallback(() => {
    if (!currentInviteLinkUrl) return

    copyText(currentInviteLinkUrl)
  }, [currentInviteLinkUrl, copyText])

  const handleShareInviteLinkPress = useCallback(() => {
    if (!currentInviteLinkUrl) return

    openShare({
      title: '邀请你加入表单协作',
      url: currentInviteLinkUrl,
      activityItemSources: [
        {
          placeholderItem: { type: 'url', content: currentInviteLinkUrl },
          item: { default: { type: 'url', content: currentInviteLinkUrl } },
          linkMetadata: {
            originalUrl: currentInviteLinkUrl,
            url: currentInviteLinkUrl,
            title: '邀请你加入表单协作',
          },
        },
      ],
    }).catch(noop)
  }, [currentInviteLinkUrl])

  const sections = useMemo(() => {
    const groups: TableSection[] = []

    // Section 1: Switch for enabling invite links
    groups.push({
      title: '',
      rows: [
        {
          title: '邀请链接',
          type: 'switch',
          switchValue: isEnabled,
          onValueChange: handleToggleSwitch,
        },
      ],
    })

    if (!isEnabled) {
      return groups
    }

    // Section 2: Form role selection menu
    if (selectedFormRole) {
      groups.push({
        title: '',
        rows: [
          {
            title: '邀请受邀人为',
            type: 'menu',
            menuItems: formRoleMenuItems,
            menuSelectedTitle: formRolesMap[selectedFormRole]?.name,
            onMenuItemSelect: handleFormRoleMenuItemSelect,
          },
        ],
      })
    }

    // Section 3: User role selection menu (only when canUseOrgInviteLink)
    if (canUseOrgInviteLink && selectedUserRole) {
      groups.push({
        title: '受邀的非企业成员将被添加为企业的',
        rows: [
          {
            title: '加入企业后的身份',
            type: 'menu',
            menuItems: userRoleMenuItems,
            menuSelectedTitle: userRolesMap[selectedUserRole]?.label,
            onMenuItemSelect: handleUserRoleMenuItemSelect,
          },
        ],
      })
    }

    // Section 4: URL display
    groups.push({
      title: '邀请链接',
      rows: [
        {
          title: currentInviteLinkUrl || '',
          subTitle: currentInviteLinkUrl ? null : '加载中...',
        },
      ],
    })

    // Section 5: Action rows
    if (currentInviteLinkUrl) {
      const actionRows = [
        {
          type: 'action' as const,
          icon: 'doc.on.doc',
          title: '复制链接',
          onPress: handleCopyInviteLinkPress,
        },
      ]

      if (Platform.OS !== 'web') {
        actionRows.push({
          type: 'action' as const,
          icon: 'square.and.arrow.up',
          title: '分享链接',
          onPress: handleShareInviteLinkPress,
        })
      }

      groups.push({
        title: '',
        rows: actionRows,
      })
    }

    return groups
  }, [
    isEnabled,
    selectedFormRole,
    formRoleMenuItems,
    formRolesMap,
    handleFormRoleMenuItemSelect,
    canUseOrgInviteLink,
    selectedUserRole,
    userRoleMenuItems,
    userRolesMap,
    handleUserRoleMenuItemSelect,
    currentInviteLinkUrl,
    handleToggleSwitch,
    handleCopyInviteLinkPress,
    handleShareInviteLinkPress,
  ])

  const footerText = useMemo(() => {
    if (!isEnabled || !currentInviteLinkUrl) return undefined

    let text = '链接长期有效，关闭邀请后立即失效'
    if (currentInviteLink && !currentInviteLink.formRole?.predefined) {
      text += `，仅能邀请 ${currentBillingAccountName} 中的企业成员为自定义角色`
    }
    return text
  }, [isEnabled, currentInviteLinkUrl, currentInviteLink, currentBillingAccountName])

  return <TableView sections={sections} headerText="通过链接邀请他人协作管理表单" footerText={footerText} />
}
